import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControl, TextField, Tooltip } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

import { isValidIPv4 } from "./validation";

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    fields: Record<string, unknown>;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

export const EditFingerprintConfigurationDialog: React.FC<Props> = ({ open, onClose, siteId, fields, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [ipAddress, setIpAddress] = useState("");
    const [periodicSyncEnabled, setPeriodicSyncEnabled] = useState(false);
    const [biometricDeviceValidationEnabled, setBiometricDeviceValidationEnabled] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setIpAddress(typeof fields.ipAddress === "string" ? fields.ipAddress : "");
            setPeriodicSyncEnabled(Boolean(fields.periodicSyncEnabled));
            setBiometricDeviceValidationEnabled(Boolean(fields.biometricDeviceValidationEnabled));
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const save = () => {
        if (!isValidIPv4(ipAddress)) {
            setErrors(["Fingerprint service IP address must be a valid IPv4 address."]);
            return;
        }

        setSaving(true);
        siteApi
            .updateCommissioning(siteId, [
                {
                    name: "fingerprintConfiguration",
                    items: { config: { fields: { ipAddress, periodicSyncEnabled, biometricDeviceValidationEnabled } } },
                },
            ])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar("Fingerprint configuration updated", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to update fingerprint configuration", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText="Edit Fingerprint Configuration">
                    <FormField label="IP Address" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={ipAddress}
                                onChange={(event) => setIpAddress(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="Periodic Sync">
                        <Checkbox
                            checked={periodicSyncEnabled}
                            color="primary"
                            onChange={(event) => setPeriodicSyncEnabled(event.target.checked)}
                        />
                        <Tooltip title="Nightly full resync of every user's biometric data from the cloud">
                            <InfoOutlined style={{ fontSize: "1rem", opacity: 0.5, cursor: "help" }} />
                        </Tooltip>
                    </FormField>
                    <FormField label="Device Validation">
                        <Checkbox
                            checked={biometricDeviceValidationEnabled}
                            color="primary"
                            onChange={(event) => setBiometricDeviceValidationEnabled(event.target.checked)}
                        />
                        <Tooltip title="Uncheck to disable automatic serial number detection for the connected device">
                            <InfoOutlined style={{ fontSize: "1rem", opacity: 0.5, cursor: "help" }} />
                        </Tooltip>
                    </FormField>
                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
